import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Stack,
  Chip,
  Avatar,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { useTranslation } from "react-i18next";
import { getInitials } from "../../../utils/utils";
import { tPlural } from "../utils/tPlural";

const MAX_VISIBLE_MEMBERS = 5;

const roleLabelKey = {
  owner: "boardMemberRoleOwner",
  admin: "boardMemberRoleAdmin",
  member: "boardMemberRoleMember",
  viewer: "boardMemberRoleViewer",
};

function formatRelativeBoardDate(iso, t) {
  if (!iso) return null;
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return t("boardUpdatedJustNow");
    if (diffMinutes < 60) return t("boardUpdatedMinutesAgo", { count: diffMinutes });
    if (diffHours < 24) return t("boardUpdatedHoursAgo", { count: diffHours });
    if (diffDays === 1) return t("boardUpdatedYesterday");
    if (diffDays < 7) return t("boardUpdatedDaysAgo", { count: diffDays });

    return t("boardUpdatedAt", {
      date: date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }),
    });
  } catch {
    return null;
  }
}

function MemberAvatarStack({ members = [], memberCount = 0, max = MAX_VISIBLE_MEMBERS }) {
  const theme = useTheme();
  const { t } = useTranslation("teamTasks");
  const visible = members.slice(0, max);
  const overflow = Math.max(0, (memberCount || members.length) - visible.length);

  if (!visible.length) {
    return (
      <Tooltip title={t("boardListNoMembers")}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: "text.disabled",
            fontSize: "0.75rem",
          }}
        >
          <GroupOutlinedIcon sx={{ fontSize: 16 }} />
          <span>{t("boardListNoMembers")}</span>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={tPlural(t, "boardListMembersTooltip", memberCount || members.length, {
        names: visible.map((m) => m.userName || t("commentUnknownAuthor")).join(", "),
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Stack direction="row" alignItems="center">
          {visible.map((member, index) => (
            <Avatar
              key={member.id}
              src={member.userAvatarUrl || undefined}
              sx={{
                width: 26,
                height: 26,
                fontSize: "0.65rem",
                fontWeight: 700,
                border: `2px solid ${theme.palette.background.paper}`,
                ml: index > 0 ? -0.75 : 0,
                zIndex: visible.length - index,
              }}
            >
              {getInitials(member.userName || member.participantId)}
            </Avatar>
          ))}
          {overflow > 0 ? (
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "0.62rem",
                fontWeight: 700,
                ml: -0.75,
                bgcolor: alpha(theme.palette.text.primary, 0.1),
                color: "text.secondary",
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            >
              +{overflow}
            </Avatar>
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {tPlural(t, "boardListMemberCount", memberCount || members.length)}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function StatChip({ icon, label, color = "default", variant = "outlined" }) {
  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      color={color}
      variant={variant}
      sx={{
        height: 24,
        fontSize: "0.68rem",
        fontWeight: 700,
        "& .MuiChip-icon": { fontSize: 14, ml: 0.5 },
      }}
    />
  );
}

const BoardListCard = ({
  board,
  onOpen,
  onEdit,
  onDelete,
  onOpenPopup,
  onOpenMembers,
  onOpenAnalytics,
  onOrganizeColumns,
  canManage,
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const stats = board.stats || {};
  const updatedLabel = formatRelativeBoardDate(board.updatedAt || board.createdAt, t);
  const roleKey = board.currentUserRole ? roleLabelKey[board.currentUserRole] : null;

  const descriptionText = useMemo(() => {
    if (board.description?.trim()) return board.description.trim();
    if (stats.cardCount === 0) return t("boardListCardEmptyHint");
    return t("boardListCardNoDescription");
  }, [board.description, stats.cardCount, t]);

  const openCardLabel =
    stats.openCardCount > 0
      ? tPlural(t, "boardListOpenTasks", stats.openCardCount)
      : stats.cardCount > 0
        ? t("boardListAllDone")
        : t("boardListNoTasksYet");

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 3,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.22s, border-color 0.22s, transform 0.18s",
        "&:hover": {
          boxShadow: theme.palette.mode === "dark" ? 8 : 6,
          borderColor: alpha(theme.palette.primary.main, 0.45),
          transform: "translateY(-3px)",
          "& .board-card-cta": {
            color: "primary.main",
            transform: "translateX(2px)",
          },
        },
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.light, 0.7)})`,
        }}
      />

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setMenuAnchor(e.currentTarget);
        }}
        sx={{
          position: "absolute",
          top: 12,
          right: 10,
          zIndex: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          boxShadow: 1,
          "&:hover": { bgcolor: "background.paper" },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { minWidth: 260 } }}
      >
        {onOpenMembers ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOpenMembers(board);
            }}
          >
            <ListItemIcon>
              <GroupOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("boardMembersMenu")} secondary={t("boardMembersMenuHint")} />
          </MenuItem>
        ) : null}
        {onOpenAnalytics ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOpenAnalytics(board);
            }}
          >
            <ListItemIcon>
              <InsightsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("boardAnalytics")} secondary={t("boardAnalyticsMenuHint")} />
          </MenuItem>
        ) : null}
        {canManage && onOrganizeColumns ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOrganizeColumns(board);
            }}
          >
            <ListItemIcon>
              <ViewColumnOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("organizeColumns")} secondary={t("organizeColumnsMenuHint")} />
          </MenuItem>
        ) : null}
        {onOpenPopup ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOpenPopup(board);
            }}
          >
            <ListItemIcon>
              <OpenInNewOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("openInPopup")} secondary={t("openInPopupHint")} />
          </MenuItem>
        ) : null}
        {canManage ? <Divider /> : null}
        {canManage ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onEdit(board);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("editBoard")} secondary={t("editBoardMenuHint")} />
          </MenuItem>
        ) : null}
        {canManage ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDelete(board);
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary={t("deleteBoard")} secondary={t("deleteBoardMenuHint")} />
          </MenuItem>
        ) : null}
      </Menu>

      <CardActionArea onClick={() => onOpen(board)} sx={{ flex: 1, alignItems: "stretch" }}>
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: 2.25,
            pr: 5.5,
            "&:last-child": { pb: 2.25 },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                flexShrink: 0,
              }}
            >
              <ViewKanbanOutlinedIcon color="primary" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={800} noWrap sx={{ maxWidth: "100%" }}>
                  {board.name}
                </Typography>
                {board.isPrivate ? (
                  <Tooltip title={t("boardListPrivateHint")}>
                    <LockOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </Tooltip>
                ) : null}
              </Stack>
              {roleKey ? (
                <Chip
                  size="small"
                  label={t(roleKey)}
                  sx={{
                    mt: 0.5,
                    height: 20,
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: "primary.main",
                    border: "none",
                  }}
                />
              ) : null}
            </Box>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              lineHeight: 1.6,
              minHeight: 44,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {descriptionText}
          </Typography>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <StatChip
              icon={<AssignmentOutlinedIcon />}
              label={openCardLabel}
              color={stats.openCardCount > 0 ? "primary" : "default"}
            />
            {stats.overdueCount > 0 ? (
              <StatChip
                icon={<WarningAmberOutlinedIcon />}
                label={tPlural(t, "boardListOverdueTasks", stats.overdueCount)}
                color="warning"
              />
            ) : null}
            {stats.columnCount > 0 ? (
              <StatChip
                icon={<ViewColumnOutlinedIcon />}
                label={tPlural(t, "boardListColumnCount", stats.columnCount)}
              />
            ) : null}
          </Stack>

          <Box sx={{ mt: "auto" }}>
            <MemberAvatarStack
              members={board.members || []}
              memberCount={board.memberCount || board.members?.length || 0}
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                mt: 1.75,
                pt: 1.5,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {updatedLabel}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                className="board-card-cta"
                sx={{
                  color: "text.secondary",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  transition: "color 0.2s, transform 0.2s",
                }}
              >
                {t("openBoard")}
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default BoardListCard;
